import { useEffect, useState } from "react";
import {
  CalendarPlus,
  CheckCircle2,
  ClipboardCheck,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import { useAuth } from "../auth/AuthProvider";
import { useOrganization } from "../organization/useOrganization";
import { useSales } from "../sales/useSales";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState, StatCard } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import { TeachingFeedbackPanel } from "../learning/TeachingFeedbackPanel";
import { useScheduling, useSchedulingActions } from "./useScheduling";
import type { CreateScheduleInput, LessonSchedule } from "./scheduling.types";
import { scheduleWindowError } from "./scheduleDateTime";

function localDateTime(date: Date) {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
function defaultLessonWindow() {
  const start = new Date();
  start.setSeconds(0, 0);
  start.setMinutes(0);
  start.setHours(start.getHours() + 1);
  if (start.getHours() >= 23) {
    start.setDate(start.getDate() + 1);
    start.setHours(10);
  } else if (start.getHours() < 8) {
    start.setHours(10);
  }
  return { start, end: new Date(start.getTime() + 60 * 60000) };
}
export function TeachingPage() {
  const { session } = useAuth();
  const org = useOrganization();
  const sales = useSales();
  const scheduling = useScheduling();
  const actions = useSchedulingActions();
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [consumeUnits, setConsumeUnits] = useState<Record<string, number>>({});
  const [lessonWindow] = useState(defaultLessonWindow);
  const form = useForm<CreateScheduleInput>({
    defaultValues: {
      memberId: "",
      coachId: session?.profile.id ?? "",
      venueId: "",
      packageId: "",
      startAt: localDateTime(lessonWindow.start),
      endAt: localDateTime(lessonWindow.end),
    },
  });
  const selectedMemberId = useWatch({ control: form.control, name: "memberId" });
  useEffect(() => {
    if (!session || !org.data || !selectedMemberId) return;
    const member = org.data.accounts.find((item) => item.id === selectedMemberId);
    if (!member) return;
    const authorized = new Set([
      ...(member.serviceCoachIds ?? []),
      ...(member.coachId ? [member.coachId] : []),
    ]);
    const current = form.getValues("coachId");
    if (session.profile.role === "employee" && authorized.has(session.profile.id)) {
      if (current !== session.profile.id) form.setValue("coachId", session.profile.id);
      return;
    }
    if (!authorized.has(current)) form.setValue("coachId", member.coachId ?? "");
  }, [form, org.data, selectedMemberId, session]);
  if (!session || !org.data || !sales.data || !scheduling.data)
    return <PageSkeleton />;
  const members = org.data.accounts.filter((a) =>
    a.role === "member" && a.status === "active" &&
    (session.profile.role === "super" ||
      (session.profile.role === "manager" && a.storeId === session.profile.storeId) ||
      (session.profile.role === "employee" &&
        (a.serviceCoachIds?.includes(session.profile.id) || a.coachId === session.profile.id))),
  );
  const selectedMember = members.find((item) => item.id === selectedMemberId);
  const selectedServiceCoachIds = new Set([
    ...(selectedMember?.serviceCoachIds ?? []),
    ...(selectedMember?.coachId ? [selectedMember.coachId] : []),
  ]);
  const coaches = org.data.accounts.filter(
    (a) =>
      (a.role === "employee" || a.role === "manager") &&
      a.status === "active" &&
      (selectedMember
        ? selectedServiceCoachIds.has(a.id)
        : session.profile.role === "super" ||
        (session.profile.role === "employee" && a.id === session.profile.id) ||
        (session.profile.role === "manager" && a.storeId === session.profile.storeId)),
  );
  const venues = scheduling.data.venues.filter(
    (v) => v.active && v.storeId === (selectedMember?.storeId ?? session.profile.storeId),
  );
  const packages = sales.data.packages.filter(
    (p) =>
      p.status === "active" &&
      (!selectedMemberId || p.memberId === selectedMemberId) &&
      members.some((m) => m.id === p.memberId) &&
      p.usedUnits < p.totalUnits,
  );
  const schedules = scheduling.data.schedules.filter(
    (s) => session.profile.role === "super" ||
      (session.profile.role === "manager" && s.storeId === session.profile.storeId) ||
      (session.profile.role === "employee" && s.coachId === session.profile.id),
  );
  const names = Object.fromEntries(
    org.data.accounts.map((a) => [a.id, a.name]),
  );
  const venueNames = Object.fromEntries(venues.map((v) => [v.id, v.name]));
  const run = async (action: () => Promise<unknown>, success: string) => {
    setError("");
    setNotice("");
    try {
      await action();
      setNotice(success);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "操作失败");
    }
  };
  return (
    <div className="page-stack organization-page">
      <header className="page-heading">
        <div>
          <span>{session.profile.role === "manager" ? "门店教学与会员服务" : "今日工作与会员服务"}</span>
          <h1>教学</h1>
          <p>员工教练和承担教学工作的店长均可完成排课、签到、扣课、撤销、反馈与成长影像闭环。</p>
        </div>
        <StatusBadge tone="success">本店履约</StatusBadge>
      </header>
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      <div className="stat-grid">
        <StatCard
          label="排课"
          value={`${schedules.filter((s) => s.status === "scheduled").length}`}
          detail="待签到"
        />
        <StatCard
          label="已签到"
          value={`${schedules.filter((s) => s.status === "checked_in").length}`}
          detail="待确认课消"
        />
        <StatCard
          label="待撤销审批"
          value={`${schedules.filter((s) => s.status === "reversal_pending").length}`}
          detail="超过10分钟"
        />
      </div>
      <Card className="management-card">
        <div className="card-heading">
          <CalendarPlus />
          <div>
            <span>三重排他检查</span>
            <h2>创建排课</h2>
          </div>
        </div>
        <form
          className="v4-form"
          onSubmit={form.handleSubmit(async (input) => {
            const validationError = scheduleWindowError(input.startAt, input.endAt);
            if (validationError) {
              setNotice("");
              setError(validationError);
              return;
            }
            await run(
              () => actions.createSchedule(input),
              "排课成功，会员、教练和场地均无冲突。",
            );
          })}
        >
          <div className="form-split">
            <label className="v4-field">
              <span>会员</span>
              <select required {...form.register("memberId")}>
                <option value="">请选择</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="v4-field">
              <span>教练</span>
              <select required {...form.register("coachId")}>
                {coaches.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-split">
            <label className="v4-field">
              <span>课程包</span>
              <select
                required
                {...form.register("packageId")}
              >
                <option value="">请选择</option>
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.beneficiaryName || names[p.memberId]} · {p.productName} · 剩余
                    {p.totalUnits - p.usedUnits}
                  </option>
                ))}
              </select>
              <small>课程包归属不限制本次授课教练；课消流水记录上方所选实际教练。</small>
            </label>
            <label className="v4-field">
              <span>场地/工位</span>
              <select required {...form.register("venueId")}>
                <option value="">请选择</option>
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} · {v.type === "court" ? "场地" : "工位"}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-split">
            <label className="v4-field">
              <span>开始时间</span>
              <input required type="datetime-local" {...form.register("startAt")} />
            </label>
            <label className="v4-field">
              <span>结束时间</span>
              <input required type="datetime-local" {...form.register("endAt")} />
            </label>
          </div>
          <Button disabled={actions.pending || !packages.length}>
            <CalendarPlus size={16} />
            检查冲突并排课
          </Button>
        </form>
        {!packages.length && (
          <p className="field-error">当前没有可用课程包，请先完成购课收款。</p>
        )}
      </Card>
      <ScheduleList
        schedules={schedules}
        names={names}
        venueNames={venueNames}
        consumptions={scheduling.data.consumptions}
        reasons={reasons}
        setReasons={setReasons}
        consumeUnits={consumeUnits}
        setConsumeUnits={setConsumeUnits}
        pending={actions.pending}
        onCheckIn={(id) => run(() => actions.checkIn(id), "签到成功。")}
        onCancel={(id) =>
          window.confirm("确认取消该课程？临近开课可能会被记录为晚取消。") &&
          run(() => actions.cancelSchedule(id), "课程已按3小时规则取消。")
        }
        onConsume={(id, units) =>
          window.confirm(
            `确认本节课程已经完成？确认后将扣减${units}课时并生成课消流水。`,
          ) &&
          run(
            () => actions.consume({ id, key: crypto.randomUUID(), units }),
            `课消成功，已扣减${units}课时并生成流水。`,
          )
        }
        onReverse={(consumptionId) =>
          window.confirm("确认申请撤销本次课消？超过10分钟将进入店长审批。") &&
          run(
            () =>
              actions.requestReversal({
                id: consumptionId,
                reason: reasons[consumptionId] || "操作纠错",
              }),
            "撤销已处理；10分钟内直接退课时，超时进入店长审批。",
          )
        }
      />
      <TeachingFeedbackPanel />
    </div>
  );
}
function ScheduleList({
  schedules,
  names,
  venueNames,
  consumptions,
  reasons,
  setReasons,
  consumeUnits,
  setConsumeUnits,
  pending,
  onCheckIn,
  onCancel,
  onConsume,
  onReverse,
}: {
  schedules: LessonSchedule[];
  names: Record<string, string>;
  venueNames: Record<string, string>;
  consumptions: { id: string; scheduleId: string; status: string }[];
  reasons: Record<string, string>;
  setReasons: (v: Record<string, string>) => void;
  consumeUnits: Record<string, number>;
  setConsumeUnits: (v: Record<string, number>) => void;
  pending: boolean;
  onCheckIn: (id: string) => void;
  onCancel: (id: string) => void;
  onConsume: (id: string, units: number) => void;
  onReverse: (id: string) => void;
}) {
  if (!schedules.length)
    return (
      <EmptyState
        title="暂无排课"
        description="创建排课后会显示签到和课消操作。"
      />
    );
  return (
    <Card className="directory-card">
      <div className="card-heading">
        <ClipboardCheck />
        <div>
          <span>状态机驱动</span>
          <h2>课程履约记录</h2>
        </div>
      </div>
      <div className="schedule-list">
        {schedules
          .slice()
          .reverse()
          .map((s) => {
            const consumption = consumptions.find((c) => c.scheduleId === s.id);
            return (
              <article key={s.id}>
                <div>
                  <span>{new Date(s.startAt).toLocaleString("zh-CN")}</span>
                  <h3>
                    {names[s.memberId]} · 教练{names[s.coachId]}
                  </h3>
                  <p>
                    {venueNames[s.venueId]} ·{" "}
                    {Math.round(
                      (new Date(s.endAt).getTime() -
                        new Date(s.startAt).getTime()) /
                        60000,
                    )}
                    分钟
                  </p>
                </div>
                <StatusBadge
                  tone={
                    s.status === "completed"
                      ? "success"
                      : s.status === "reversal_pending"
                        ? "warning"
                        : s.status === "cancelled"
                          ? "danger"
                          : "neutral"
                  }
                >
                  {scheduleStatus(s.status)}
                </StatusBadge>
                <div className="schedule-actions">
                  {s.status === "scheduled" && (
                    <>
                      <Button
                        onClick={() => onCheckIn(s.id)}
                        disabled={pending}
                      >
                        <CheckCircle2 size={15} />
                        签到
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => onCancel(s.id)}
                        disabled={pending}
                      >
                        <XCircle size={15} />
                        取消
                      </Button>
                    </>
                  )}
                  {s.status === "checked_in" && (
                    <>
                      <label className="v4-field schedule-unit-field">
                        <span>扣课课时</span>
                        <input
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={consumeUnits[s.id] ?? 1}
                          onChange={(event) => setConsumeUnits({
                            ...consumeUnits,
                            [s.id]: Number(event.target.value),
                          })}
                        />
                      </label>
                      <Button
                        onClick={() => onConsume(s.id, consumeUnits[s.id] ?? 1)}
                        disabled={pending || (consumeUnits[s.id] ?? 1) <= 0 || !Number.isInteger((consumeUnits[s.id] ?? 1) * 2)}
                      >
                        确认课消
                      </Button>
                    </>
                  )}
                  {s.status === "completed" &&
                    consumption?.status === "active" && (
                      <>
                        <input
                          value={reasons[consumption.id] ?? ""}
                          onChange={(e) =>
                            setReasons({
                              ...reasons,
                              [consumption.id]: e.target.value,
                            })
                          }
                          placeholder="撤销原因"
                        />
                        <Button
                          variant="ghost"
                          onClick={() => onReverse(consumption.id)}
                          disabled={
                            pending || !(reasons[consumption.id] ?? "").trim()
                          }
                        >
                          <RotateCcw size={15} />
                          撤销课消
                        </Button>
                      </>
                    )}
                </div>
              </article>
            );
          })}
      </div>
    </Card>
  );
}
function scheduleStatus(status: LessonSchedule["status"]) {
  return {
    scheduled: "已排课",
    checked_in: "已签到",
    completed: "已课消",
    cancelled: "已取消",
    late_cancelled: "晚取消",
    reversal_pending: "撤销待审",
    reversed: "已撤销",
  }[status];
}
